import { z } from 'zod'

/**
 * Empresa — contrato _padrao-via/entidade-empresa + campos próprios do Via Tax.
 *
 * Vocabulário de regime é o FECHADO do padrão (snake_case minúsculo). O motor
 * (`@via/tax-engine`) fala SIMPLES|PRESUMIDO|REAL; a tradução acontece na montagem
 * do dossiê, não aqui.
 */

export const REGIMES_TRIBUTARIOS = [
  'mei',
  'simples_nacional',
  'lucro_presumido',
  'lucro_real',
  'lucro_arbitrado',
  'imune',
  'isenta',
] as const
export type RegimeTributario = (typeof REGIMES_TRIBUTARIOS)[number]

/** Arquitetura §5.1: serviços / indústria / comércio / misto. Nomes do padrão. */
export const AREAS_ATUACAO = ['servico', 'industria', 'comercio', 'misto'] as const
export type AreaAtuacao = (typeof AREAS_ATUACAO)[number]

export const ANEXOS_SIMPLES = ['I', 'II', 'III', 'IV', 'V'] as const
export type AnexoSimples = (typeof ANEXOS_SIMPLES)[number]

export const ORIGENS_EMPRESA = ['processos', 'manual'] as const

/**
 * Perfil dos serviços prestados, que muda o cálculo:
 *  - Lucro Presumido (IN RFB 1.700/2017, arts. 33 e 34): serviços hospitalares e de auxílio diagnóstico e terapia
 *    (sociedade empresária + normas da Anvisa) e construção com emprego de todos os materiais → 8% IRPJ / 12% CSLL;
 *    demais serviços, profissão regulamentada e construção só de mão de obra ou material parcial → 32% / 32%.
 *  - CBS/IBS (LC 214/2025): serviços de saúde −60% (arts. 128 e 130, Anexo III); profissões intelectuais −30%
 *    quando a pessoa jurídica cumpre os requisitos do art. 127 § 1º II.
 *  - Simples: construção vai para o Anexo IV (LC 123, art. 18 § 5º-C I).
 */
export const PERFIS_SERVICO = ['SAUDE_HOSPITALAR', 'SAUDE', 'PROFISSAO_INTELECTUAL', 'CONSTRUCAO_COM_MATERIAL', 'CONSTRUCAO_SEM_MATERIAL'] as const
export type PerfilServico = (typeof PERFIS_SERVICO)[number]
export const ROTULO_PERFIL_SERVICO: Record<PerfilServico, string> = {
  SAUDE_HOSPITALAR: 'Saúde: hospital, clínica, laboratório, diagnóstico e terapia',
  SAUDE: 'Saúde: consultório e demais serviços de saúde',
  PROFISSAO_INTELECTUAL: 'Profissão intelectual regulamentada (advocacia, contabilidade, engenharia…)',
  CONSTRUCAO_COM_MATERIAL: 'Construção por empreitada com todos os materiais',
  CONSTRUCAO_SEM_MATERIAL: 'Construção só de mão de obra ou com material parcial',
}
/** O que o escritório confirma para o perfil valer por inteiro (null = sem requisito extra). */
export const REQUISITO_PERFIL_SERVICO: Record<PerfilServico, string | null> = {
  SAUDE_HOSPITALAR: 'É sociedade empresária e atende às normas da Anvisa (presunção de 8% IRPJ / 12% CSLL — IN 1.700, art. 33 § 1º II a)',
  SAUDE: null,
  PROFISSAO_INTELECTUAL:
    'Sócios habilitados e fiscalizados por conselho, sem sócio pessoa jurídica, não é sócia de outra PJ, sem atividade diversa e serviço prestado pelos sócios (redução de 30% da CBS/IBS — LC 214, art. 127 § 1º II)',
  CONSTRUCAO_COM_MATERIAL: null,
  CONSTRUCAO_SEM_MATERIAL: null,
}

/**
 * Sugestão de perfil pelo item da lista da LC 116/2003 no código de serviço das NFS-e emitidas (cTribNac ou item da
 * lista). Só sugestão: o escritório confirma. Itens conferidos no texto da LC 116 (Planalto).
 */
export const SUGESTAO_PERFIL_LC116: Record<string, PerfilServico> = {
  '4.02': 'SAUDE_HOSPITALAR', // análises clínicas, patologia, radioterapia, quimioterapia, ultrassonografia, ressonância, radiologia, tomografia
  '4.03': 'SAUDE_HOSPITALAR', // hospitais, clínicas, laboratórios, sanatórios, prontos-socorros, ambulatórios
  '4.08': 'SAUDE_HOSPITALAR', // terapia ocupacional, fisioterapia e fonoaudiologia
  '5.01': 'PROFISSAO_INTELECTUAL', // medicina veterinária e zootecnia
  '7.01': 'PROFISSAO_INTELECTUAL', // engenharia, agronomia, arquitetura, urbanismo
  '7.02': 'CONSTRUCAO_SEM_MATERIAL', // execução de obras de construção civil (material: confirmar)
  '7.05': 'CONSTRUCAO_SEM_MATERIAL', // reparação, conservação e reforma de edifícios
  '17.12': 'PROFISSAO_INTELECTUAL', // administração em geral
  '17.14': 'PROFISSAO_INTELECTUAL', // advocacia
  '17.19': 'PROFISSAO_INTELECTUAL', // contabilidade
  '17.20': 'PROFISSAO_INTELECTUAL', // consultoria e assessoria econômica ou financeira
  '17.21': 'PROFISSAO_INTELECTUAL', // estatística
}

/** "040301" (cTribNac), "0403", "4.03" ou "4.3" → "4.03". Código que não parece item da lista → null. */
export function itemLc116(codigo: string | null | undefined): string | null {
  if (!codigo) return null
  const t = codigo.trim()
  const pontos = /^(\d{1,2})\.(\d{1,2})/.exec(t)
  if (pontos) return `${Number(pontos[1])}.${pontos[2]!.padStart(2, '0')}`
  const d = t.replace(/\D/g, '')
  if (d.length === 3) return `${Number(d[0])}.${d.slice(1)}`
  if (d.length >= 4) return `${Number(d.slice(0, 2))}.${d.slice(2, 4)}`
  return null
}

/** Perfil sugerido para um item da LC 116: a tabela acima; qualquer outro item 4.xx é saúde. */
export function perfilDoItemLc116(item: string | null): PerfilServico | null {
  if (!item) return null
  return SUGESTAO_PERFIL_LC116[item] ?? (item.startsWith('4.') ? 'SAUDE' : null)
}

/**
 * O que o Via Tax pode editar. Campos que vêm do Processos (CNPJ, razão social,
 * nome fantasia, situação cadastral, responsável) NÃO aparecem aqui — um schema
 * que não os declara é a garantia de que ninguém os sobrescreve pela API.
 */
export const EmpresaEditavel = z
  .object({
    regimeTributario: z.enum(REGIMES_TRIBUTARIOS).nullable(),
    areaAtuacao: z.enum(AREAS_ATUACAO).nullable(),
    anexoSimples: z.enum(ANEXOS_SIMPLES).nullable(),
    perfilServico: z.enum(PERFIS_SERVICO).nullable(),
    /** requisitos do perfil confirmados pelo escritório (Anvisa/sociedade empresária; art. 127 § 1º II) */
    requisitosPerfil: z.boolean(),
  })
  .partial()
  .strict()
  .superRefine((v, ctx) => {
    if (v.anexoSimples && v.regimeTributario && v.regimeTributario !== 'simples_nacional') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['anexoSimples'],
        message: 'Anexo do Simples só se aplica a empresa do Simples Nacional',
      })
    }
  })
export type EmpresaEditavel = z.infer<typeof EmpresaEditavel>

export const ROTULO_REGIME: Record<RegimeTributario, string> = {
  mei: 'MEI',
  simples_nacional: 'Simples Nacional',
  lucro_presumido: 'Lucro Presumido',
  lucro_real: 'Lucro Real',
  lucro_arbitrado: 'Lucro Arbitrado',
  imune: 'Imune',
  isenta: 'Isenta',
}

export const ROTULO_AREA: Record<AreaAtuacao, string> = {
  servico: 'Serviços',
  industria: 'Indústria',
  comercio: 'Comércio',
  misto: 'Misto',
}
